import styled from '@emotion/styled'
import { Drawer as MuiDrawer, ListItemButton } from '@mui/material'
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { SidebarItemsType } from '@/types/sidebar'

import Footer from './SidebarFooter'
import SidebarNav from './SidebarNav'

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
  }
`

const Brand = styled(ListItemButton)<{
  component?: React.ReactNode
  to?: string
}>`
  font-size: ${props => props.theme.typography.h5.fontSize};
  font-weight: ${props => props.theme.typography.fontWeightMedium};
  color: ${props => props.theme.sidebar.header.color};
  background-color: ${props => props.theme.sidebar.header.background};
  font-family: ${props => props.theme.typography.fontFamily};
  min-height: 56px;
  max-height: 80px;
  padding-left: ${props => props.theme.spacing(6)};
  padding-right: ${props => props.theme.spacing(6)};
  justify-content: center;
  cursor: pointer;
  flex-grow: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.12);

  ${props => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }

  &:hover {
    background-color: ${props => props.theme.sidebar.header.background};
  }
`

const Image = styled.img`
  max-width: 100%;
  max-height: 64px;
  object-fit: contain;
`

export type SidebarProps = {
  PaperProps: {
    style: {
      width: number
    }
  }
  variant?: 'permanent' | 'persistent' | 'temporary'
  open?: boolean
  onClose?: () => void
  items: {
    title: string
    pages: SidebarItemsType[]
  }[]
  showFooter?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  showFooter = true,
  ...rest
}) => {
  const { user } = useLoggedInUser('cache-first')
  const { t } = useT()
  const { search } = useLocation()

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink as any} to={`/home${search}`}>
        {user?.tenant?.logoImage?.url ? (
          <Image
            alt={t('sidebar.logo.company.alt')}
            src={user.tenant.logoImage.url}
          />
        ) : null}
      </Brand>
      <SidebarNav items={items} />
      {!!showFooter && <Footer />}
    </Drawer>
  )
}

export default Sidebar
